import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { TriageExpenseList } from "../components/triageExpenseList";
import { NewExpenseFormButton } from '../components/newExpenseFormButton';
import { useAppDispatch, useAppSelector } from "../hooks/reduxToolkit";
import { ExpensesNavigation } from '../components/expensesNavigation';
import { useExpenseSearch } from "../hooks/useExpenseSearch";
import { ExpenseModel } from "../reduxToolkit/expensesSlice";
import { useNavigateToNewExpense } from "../hooks/useNavigateToExpense";
import { deriveExpenseState } from "../../lib/expenses/expenseState";
import { listItems } from "@healthshare/shared/commands/plaid";
import { useBackendApi } from "../hooks/useBackendApi";
import { useSession } from "../providers/supabase";
import { useAsyncEffect } from "../hooks/useAsyncEffect";
import { usePlaidLinkFlow } from "../hooks/usePlaidLinkFlow";
import { usePlaidLinkEnabled } from "../providers/featureFlags";
import { setPlaidItems } from "../reduxToolkit/plaidSlice";
import { Tooltip } from "../components/tooltip";

import './expenses.scss'

type FilterMode = 'draft' | 'open' | 'all'

const FILTER_MODES: { mode: FilterMode; label: string }[] = [
  { mode: 'draft', label: 'Draft' },
  { mode: 'open', label: 'Open' },
  { mode: 'all', label: 'All' },
]

const HEADER_TEXT: Record<FilterMode, string> = {
  draft: 'Draft Expenses',
  open: 'Open Expenses',
  all: 'All Expenses',
}

const EMPTY_HEADER_TEXT: Record<FilterMode, string> = {
  draft: 'No Draft Expenses',
  open: 'No Open Expenses',
  all: 'No Expenses',
}

export function Expenses() {
  const location = useLocation()
  const navigateToNewExpense = useNavigateToNewExpense()

  const expenses = useAppSelector((state) => state.expenses.expenses)
  const incidents = useAppSelector((state) => state.incidents.incidents)

  // Build a set of closed incident IDs
  const closedIncidentIds = useMemo(() =>
    new Set(incidents.filter(i => i.closed_at).map(i => i.id)),
    [incidents]
  )

  const [highlightExpenseId, setHighlightExpenseId] = useState<string | null>(location.state?.expense_id || null)
  const [filterMode, setFilterMode] = useState<FilterMode>('draft')

  // Search across all expenses (including those from closed incidents)
  const [
    results,
    search,
    { query }
  ] = useExpenseSearch(expenses)

  // The three filter buckets:
  //  - draft: only auto-imported, unreviewed drafts
  //  - open:  draft + attached (excludes submitted / closed-incident expenses)
  //  - all:   every expense, including submitted and closed-incident ones
  const filteredExpenses = useMemo(() => {
    if (filterMode === 'all') {
      return expenses
    }
    return expenses.filter((e) => {
      const state = deriveExpenseState(e, closedIncidentIds)
      return filterMode === 'draft' ? state === 'draft' : state !== 'closed'
    })
  }, [expenses, closedIncidentIds, filterMode])

  let headerText = HEADER_TEXT[filterMode]
  if (results) {
    // `results` is an array (possibly empty) once a search has run, so distinguish
    // "no matches" from "search results" rather than always showing the latter.
    headerText = results.length === 0 ? 'No Matching Expenses' : 'Search Results'
  } else if (filteredExpenses.length === 0) {
    headerText = EMPTY_HEADER_TEXT[filterMode]
  }

  // Search results (which include expenses from closed incidents) override the filter.
  const expensesToShow: ExpenseModel[] = results ?? filteredExpenses

  // The Draft tab imports transactions from linked bank/HSA accounts. When it's
  // empty, guide the user to link an account (or explain the sync delay if they
  // already have one) instead of showing a bare empty list. Shown to everyone —
  // users whose plan lacks the feature get a disabled link button with a tooltip
  // (see DraftExpensesEmptyState) rather than the empty state being hidden.
  const showDraftEmptyState = filterMode === 'draft' && !results && filteredExpenses.length === 0

  return <div className="expenses">
  <div className="row expenses__title">
    <div className="col-12">
      <h1>Expenses</h1>
      <p>
        Review and categorize expenses imported from your HSA or credit card. Assign expenses to incidents to track your reimbursements.
      </p>

      <ExpensesNavigation fuseSearch={{
            query,
            search: (newQuery) => {
              setHighlightExpenseId(null)
              search(newQuery)
            }
          }} />
    </div>
  </div>
  <hr />
  <div className="row expenses__row">
    <div className="col-12">
      <div className="expenses__row-header">
        <div>
          <h6>
            {headerText}
          </h6>

          <div className={`btn-group btn-group-sm ${results ? 'opacity-50' : ''}`} role="group" aria-label="Filter expenses by state">
            {FILTER_MODES.map(({ mode, label }) => (
              <button
                key={mode}
                type="button"
                className={`btn ${filterMode === mode ? 'btn-primary' : 'btn-outline-primary'}`}
                aria-pressed={filterMode === mode}
                aria-describedby={results ? 'expenses-filter-disabled-hint' : undefined}
                disabled={!!results}
                title={results ? 'Clear the search to filter by state' : undefined}
                onClick={() => setFilterMode(mode)}
              >
                {label}
              </button>
            ))}
            {results && (
              <span id="expenses-filter-disabled-hint" className="visually-hidden">
                Clear the search to filter by state
              </span>
            )}
          </div>
        </div>

        <div>
          <NewExpenseFormButton allowNewIncident
            onInserted={navigateToNewExpense} />
        </div>
      </div>
      {showDraftEmptyState
        ? <DraftExpensesEmptyState />
        : <TriageExpenseList expenses={expensesToShow} highlightExpenseId={highlightExpenseId} closedIncidentIds={closedIncidentIds} />}
    </div>
  </div>
  </div>
}

/**
 * Empty state for the Draft tab when the user has no draft (auto-imported)
 * transactions. Shown to everyone.
 *
 *  - No account linked yet → a call to action that starts the Plaid link flow
 *    immediately, right here on the page. When the user's plan doesn't grant the
 *    feature the button is disabled with a "contact us to enable sync" tooltip.
 *  - An account is linked   → a muted note that syncing can take a few minutes
 *    and only medical-coded transactions are imported, plus a link to manage
 *    connected accounts.
 */
export function DraftExpensesEmptyState() {
  const dispatch = useAppDispatch()
  const api = useBackendApi()
  const isAuthenticated = !!useSession()?.access_token
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const items = useAppSelector((s) => s.plaid.items)
  const loaded = useAppSelector((s) => s.plaid.loaded)
  const canLink = usePlaidLinkEnabled()

  const { startLink, linking, error, SubscriptionPromptModal } = usePlaidLinkFlow()

  // The Plaid slice is only populated when the user visits Connected Accounts,
  // and it's persisted, so on this page it may be missing or stale. Refetch on
  // mount (like PlaidConnectedAccountsSection) so the branch we show reflects
  // the real linked-account state rather than a leftover persisted flag.
  const { loading, error: fetchError } = useAsyncEffect(async () => {
    if (!membershipId || !isAuthenticated) return
    const data = await listItems(api, membershipId)
    dispatch(setPlaidItems(data.items ?? []))
  }, [membershipId, isAuthenticated, api, dispatch])

  // Until we have authoritative data, don't guess: a spinner on first load, and
  // an error (rather than a misleading "no account" CTA) if the fetch fails
  // before we've ever loaded the account list. Once `loaded` is true we render
  // from last-known-good items even if a later refresh fails.
  if (!loaded && loading) {
    return <div className="loading-bar" />
  }
  if (!loaded && fetchError) {
    return (
      <div className="expenses__draft-empty text-center py-5">
        <div className="alert alert-danger" role="alert">
          Could not load your linked accounts. Please try again.
        </div>
      </div>
    )
  }

  const hasLinkedAccount = items.length > 0

  return (
    <div className="expenses__draft-empty text-center text-muted py-5">
      {hasLinkedAccount ? (
        <>
          <p className="mb-1">
            No draft transactions yet. Syncing can take a few minutes after
            linking, and we only import medical-coded transactions.
          </p>
          <p className="mb-0">
            <Link to="/connectedAccounts">Manage your linked accounts</Link>
          </p>
        </>
      ) : (
        <>
          <p>
            Link a bank or HSA account to automatically import medical
            transactions as draft expenses.
          </p>
          <Tooltip
            tooltip={canLink ? undefined : 'contact us to enable sync'}
            className="d-inline-block"
          >
            <button
              type="button"
              className="btn btn-primary"
              onClick={startLink}
              disabled={!canLink || linking || !membershipId}
            >
              <i className="material-icons align-middle" style={{ fontSize: 18, marginRight: 6 }}>link</i>
              {linking ? 'Connecting…' : 'Link a bank account'}
            </button>
          </Tooltip>
        </>
      )}

      {error && (
        <div className="alert alert-danger mt-3" role="alert">{error}</div>
      )}

      <SubscriptionPromptModal />
    </div>
  )
}
